import { useState, useEffect, useRef } from 'react';
import { fetchWithAuth } from '../config/api';
import '../css/Phase2RealizationSelection.css';
import { MOCK_MODE } from '../config/mock';
import EditableLabel from './EditableLabel';
import { useLabels } from '../config/labelsContext';

interface RealizationMethod {
  id: string;
  name: string;
  description: string;
  patent_count: number;
}

interface Phase2RealizationSelectionProps {
  sessionId: string;
  onComplete: (data: unknown) => void;
  setIsLoading: (loading: boolean) => void;
  setLoadingMessage: (message: string) => void;
  showAlert?: (message: string, title?: string) => void;
}

class UserFacingError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'UserFacingError';
    this.code = code;
  }
}

const Phase2RealizationSelection = ({
  sessionId,
  onComplete,
  setIsLoading,
  setLoadingMessage,
  showAlert,
}: Phase2RealizationSelectionProps) => {
  const { labels } = useLabels();
  const realizationExtractingMessage = labels.phase2realization.extractingMessage;
  const sessionDeletedMessage = labels.phase2realization.sessionDeletedMessage;
  const sessionMissingMessage = labels.phase2realization.sessionMissingMessage;
  const realizationFailedMessage = labels.phase2realization.extractFailedMessage;
  const isJsonResponse = (res: Response) => {
    const ct = (res.headers.get('content-type') || '').toLowerCase();
    return ct.includes('application/json');
  };
  const isTransientHttpStatus = (code: number) => {
    // Treat temporary gateway/proxy errors as retryable (EB deploy/restart, transient network issues).
    return code === 502 || code === 503 || code === 504 || code === 429;
  };
  const alertUI = (message: string) => {
    if (showAlert) return showAlert(message, labels.modals.noticeTitle);
    window.alert(message);
  };
  const getErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error && error.message) return error.message;
    return fallback;
  };
  const readResponsePayload = async (res: Response): Promise<Record<string, unknown> | null> => {
    if (!isJsonResponse(res)) return null;
    try {
      const payload = await res.json();
      return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  };
  const createResponseError = async (res: Response, fallback: string) => {
    const payload = await readResponsePayload(res);
    const detail =
      (typeof payload?.detail === 'string' && payload.detail.trim()) ||
      (typeof payload?.error_message === 'string' && payload.error_message.trim()) ||
      '';

    if (res.status === 410) {
      return new UserFacingError('session_deleted', detail || sessionDeletedMessage);
    }
    if (res.status === 404) {
      return new UserFacingError('session_missing', detail || sessionMissingMessage);
    }
    if (res.status === 503) {
      return new UserFacingError(
        'service_unavailable',
        detail || labels.phase2realization.serverBusy
      );
    }
    if (detail) {
      return new UserFacingError('request_failed', detail);
    }
    return new UserFacingError('request_failed', fallback);
  };
  const applyRealizationState = (sessionState: Record<string, unknown>) => {
    const step = Number(sessionState?.current_step ?? 0);
    if (step < 16) return false;

    const rms: RealizationMethod[] = Array.isArray(sessionState?.realization_methods)
      ? (sessionState.realization_methods as RealizationMethod[])
      : [];
    const serverSelected: string[] = Array.isArray(sessionState?.selected_realization_ids)
      ? (sessionState.selected_realization_ids as string[])
      : [];

    setMethods(rms);
    setSelectedIds(serverSelected.length ? serverSelected : rms.map((m) => m.id));
    setLoading(false);
    return true;
  };
  const [methods, setMethods] = useState<RealizationMethod[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [inlineLoadingMessage, setInlineLoadingMessage] = useState('');
  const [processing, setProcessing] = useState(false);
  const extractedForSessionRef = useRef<string | null>(null);
  const extractInFlightRef = useRef(false);
  const proceedInFlightRef = useRef(false);
  const resumeCheckedForSessionRef = useRef<string | null>(null);
  const resumeFlowActiveRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    // StrictMode対策：同一sessionで二重に叩かない
    if (extractedForSessionRef.current === sessionId) return;
    extractedForSessionRef.current = sessionId;

    setLoading(true);
    setInlineLoadingMessage(labels.phase2realization.sessionLoadingMessage);

    if (MOCK_MODE) {
      const rms: RealizationMethod[] = [
        {
          id: 'rm-1',
          name: '位置補正アルゴリズム',
          description: '環境変動に強い着陸位置補正',
          patent_count: 42,
        },
        {
          id: 'rm-2',
          name: '電波センシング',
          description: '距離・相対位置を取得するセンシング技術',
          patent_count: 58,
        },
        {
          id: 'rm-3',
          name: 'リアルタイム制御',
          description: '制御精度を向上させるフィードバック制御',
          patent_count: 33,
        },
      ];
      setMethods(rms);
      setSelectedIds(rms.map((m) => m.id));
      setLoading(false);
      setIsLoading(false);
      setLoadingMessage('');
      return;
    }

    // First, try to resume based on server-side session state (refresh-safe).
    // If resume doesn't apply, fall back to normal flow.
    resumeOrStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const fetchSessionState = async () => {
    const res = await fetchWithAuth(`/api/phase3/session/${encodeURIComponent(sessionId)}`, {
      method: 'GET',
    });
    if (!res.ok) throw await createResponseError(res, labels.phase2realization.sessionInfoError);
    // Some gateways/CDNs can return an HTML error page with a 200.
    // Never attempt JSON parsing unless the response is actually JSON.
    if (!isJsonResponse(res)) {
      throw new UserFacingError('invalid_response', labels.phase2realization.sessionInfoError);
    }
    return (await res.json()) as Record<string, unknown>;
  };
  const tryRestoreCompletedRealization = async () => {
    const sessionState = await fetchSessionState();
    return applyRealizationState(sessionState);
  };

  const pollUntilDone = async (
    statusUrl: string,
    message: string,
    onCompleted?: (data: Record<string, unknown>) => void
  ) => {
    let delayMs = 2000;
    let transientErrs = 0;
    for (;;) {
      if (!mountedRef.current) return {};

      setIsLoading(true);
      setLoadingMessage(message);

      const statusRes = await fetchWithAuth(statusUrl, { method: 'GET' });

      if (statusRes.status === 202) {
        await new Promise((r) => setTimeout(r, delayMs));
        delayMs = Math.min(delayMs * 1.3, 10000);
        continue;
      }

      if (isTransientHttpStatus(statusRes.status)) {
        transientErrs += 1;
        // Keep waiting; do not fail the whole flow due to a brief gateway hiccup.
        await new Promise((r) => setTimeout(r, delayMs));
        // Slightly stronger backoff for repeated gateway errors.
        delayMs = Math.min(Math.floor(delayMs * 1.5), 15000);
        // Safety: after a long streak, surface an error.
        if (transientErrs >= 30) {
          throw new Error(labels.phase2realization.serverBusy);
        }
        continue;
      }

      if (!statusRes.ok)
        throw await createResponseError(statusRes, labels.phase2realization.processingError);

      transientErrs = 0;
      if (!isJsonResponse(statusRes)) {
        // Unexpected HTML/other content; treat as transient rather than crashing on JSON parse.
        transientErrs += 1;
        await new Promise((r) => setTimeout(r, delayMs));
        delayMs = Math.min(Math.floor(delayMs * 1.5), 15000);
        if (transientErrs >= 30) {
          throw new Error(labels.phase2realization.serverBusy);
        }
        continue;
      }
      const statusData = (await statusRes.json()) as Record<string, unknown>;
      if (statusData.status === 'completed') {
        if (onCompleted) onCompleted(statusData);
        return statusData;
      }

      if (statusData.status === 'failed') {
        const detail =
          (typeof statusData.error_message === 'string' && statusData.error_message.trim()) ||
          labels.phase2realization.processingError;
        throw new UserFacingError('job_failed', detail);
      }

      if (statusData.status === 'not_started') {
        await new Promise((r) => setTimeout(r, delayMs));
        delayMs = Math.min(delayMs * 1.3, 10000);
        continue;
      }

      // Fallback: some endpoints return completed payload without status field
      if (Array.isArray(statusData.realization_methods) || Array.isArray(statusData.qualities)) {
        if (onCompleted) onCompleted(statusData);
        return statusData;
      }

      throw new Error(labels.phase2realization.processingError);
    }
  };

  const resumeOrStart = async () => {
    if (!sessionId) return;
    if (resumeCheckedForSessionRef.current === sessionId) return;
    resumeCheckedForSessionRef.current = sessionId;
    resumeFlowActiveRef.current = true;

    try {
      setInlineLoadingMessage(labels.phase2realization.sessionLoadingMessage);
      setIsLoading(true);
      setLoadingMessage(labels.phase2realization.sessionLoadingMessage);
      const s = await fetchSessionState();
      const step = Number(s?.current_step ?? 0);
      const status = String(s?.status ?? '');

      // If quality judgment already completed, immediately move forward.
      if (step >= 19) {
        setIsLoading(true);
        setLoadingMessage(labels.phase2realization.qualityJudging);
        try {
          const qualRes = await fetchWithAuth(
            `/api/phase2/judge-patent-quality/status?session_id=${encodeURIComponent(sessionId)}`,
            { method: 'GET' }
          );
          if (qualRes.ok) {
            const data = await qualRes.json();
            onComplete(data);
          } else {
            onComplete({});
          }
        } catch {
          onComplete({});
        }
        return;
      }

      // If we're in the middle of components/quality pipeline, resume it.
      if (status === 'extracting_components' || status === 'judging_patent_quality' || step >= 17) {
        setProcessing(true);
        setIsLoading(true);

        // Keep inline message consistent with the global modal message.
        if (status === 'judging_patent_quality' || step >= 18) {
          setInlineLoadingMessage(labels.phase2realization.qualityJudging);
          setLoadingMessage(labels.phase2realization.qualityJudging);
        } else {
          setInlineLoadingMessage(labels.phase2realization.componentsExtracting);
          setLoadingMessage(labels.phase2realization.componentsExtracting);
        }

        // Restore selected methods if already saved on server.
        const serverSelected: string[] = Array.isArray(s?.selected_realization_ids)
          ? s.selected_realization_ids
          : [];
        if (serverSelected.length > 0) {
          setSelectedIds(serverSelected);
        }

        // 構成要素抽出: step < 18 または status が extracting_components なら実行
        if (status === 'extracting_components' || step < 18) {
          // Start if not started (safe/idempotent; backend returns 202 if already running).
          setInlineLoadingMessage(labels.phase2realization.componentsExtracting);
          setLoadingMessage(labels.phase2realization.componentsExtracting);
          const compRes = await fetchWithAuth(
            `/api/phase2/extract-components?session_id=${encodeURIComponent(sessionId)}`,
            { method: 'POST' }
          );
          if (compRes.status === 202) {
            await pollUntilDone(
              `/api/phase2/extract-components/status?session_id=${encodeURIComponent(sessionId)}`,
              labels.phase2realization.componentsExtracting
            );
          } else {
            if (!compRes.ok) throw new Error(labels.phase2realization.componentsError);
            await compRes.json().catch(() => null);
          }
        }

        // 良否判定: step < 19 なら実行して完了を待つ
        // Ensure quality judgment is completed.
        setInlineLoadingMessage(labels.phase2realization.qualityJudging);
        setLoadingMessage(labels.phase2realization.qualityJudging);
        const qualRes = await fetchWithAuth(
          `/api/phase2/judge-patent-quality?session_id=${encodeURIComponent(sessionId)}`,
          { method: 'POST' }
        );

        if (qualRes.status === 202) {
          await pollUntilDone(
            `/api/phase2/judge-patent-quality/status?session_id=${encodeURIComponent(sessionId)}`,
            labels.phase2realization.qualityJudging,
            (data) => onComplete(data)
          );
        } else if (qualRes.ok) {
          const data = await qualRes.json();
          onComplete(data);
        } else if (qualRes.status === 400) {
          throw new Error(labels.phase2realization.qualityNotReady);
        } else {
          throw new Error(labels.phase2realization.qualityError);
        }
        return;
      }

      // If realization methods are already extracted, show selection UI.
      if (status === 'extracting_realization_methods') {
        setInlineLoadingMessage(realizationExtractingMessage);
        setIsLoading(true);
        setLoadingMessage(realizationExtractingMessage);
        const data = await pollUntilDone(
          `/api/phase2/extract-realization-methods/status?session_id=${encodeURIComponent(sessionId)}`,
          realizationExtractingMessage
        );
        const rms: RealizationMethod[] = Array.isArray(data?.realization_methods)
          ? data.realization_methods
          : [];
        setMethods(rms);
        const serverSelected: string[] = Array.isArray(s?.selected_realization_ids)
          ? s.selected_realization_ids
          : [];
        setSelectedIds(serverSelected.length ? serverSelected : rms.map((m) => m.id));
        setLoading(false);
        setIsLoading(false);
        return;
      }

      if (step >= 16) {
        const rms: RealizationMethod[] = Array.isArray(s?.realization_methods)
          ? s.realization_methods
          : [];
        if (rms.length > 0) {
          setMethods(rms);
          const serverSelected: string[] = Array.isArray(s?.selected_realization_ids)
            ? s.selected_realization_ids
            : [];
          setSelectedIds(serverSelected.length ? serverSelected : rms.map((m) => m.id));
          setLoading(false);
          setIsLoading(false);
          return;
        }
        // As a fallback, query status endpoint (completed payload includes methods).
        setInlineLoadingMessage(realizationExtractingMessage);
        setIsLoading(true);
        setLoadingMessage(realizationExtractingMessage);
        const data = await pollUntilDone(
          `/api/phase2/extract-realization-methods/status?session_id=${encodeURIComponent(sessionId)}`,
          realizationExtractingMessage
        );
        const rms2: RealizationMethod[] = Array.isArray(data?.realization_methods)
          ? data.realization_methods
          : [];
        setMethods(rms2);
        setSelectedIds(rms2.map((m) => m.id));
        setLoading(false);
        setIsLoading(false);
        return;
      }

      // Otherwise, start the normal flow.
      await extractMethods({ preserveGlobalLoading: true });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Resume failed; falling back to normal flow', e);
      if (
        e instanceof UserFacingError &&
        (e.code === 'session_deleted' || e.code === 'session_missing')
      ) {
        alertUI(e.message);
        setLoading(false);
        setIsLoading(false);
        return;
      }
      await extractMethods({ preserveGlobalLoading: true });
    } finally {
      resumeFlowActiveRef.current = false;
      setIsLoading(false);
    }
  };

  const extractMethods = async ({
    preserveGlobalLoading = false,
  }: { preserveGlobalLoading?: boolean } = {}) => {
    if (!sessionId) return;

    // 多重実行ガード（保険）
    if (extractInFlightRef.current) return;
    extractInFlightRef.current = true;

    setInlineLoadingMessage(realizationExtractingMessage);
    if (!preserveGlobalLoading) {
      setIsLoading(true);
      setLoadingMessage(realizationExtractingMessage);
    } else {
      setLoadingMessage(realizationExtractingMessage);
    }

    try {
      const pollStatus = async () => {
        // Poll until the backend finishes (avoids 504s on long LLM processing)
        let delayMs = 2000;
        let transientErrs = 0;
        for (;;) {
          if (!mountedRef.current) return;

          const statusRes = await fetchWithAuth(
            `/api/phase2/extract-realization-methods/status?session_id=${encodeURIComponent(sessionId)}`,
            { method: 'GET' }
          );

          if (statusRes.status === 202) {
            await new Promise((r) => setTimeout(r, delayMs));
            delayMs = Math.min(delayMs * 1.3, 10000);
            continue;
          }

          if (isTransientHttpStatus(statusRes.status)) {
            transientErrs += 1;
            await new Promise((r) => setTimeout(r, delayMs));
            delayMs = Math.min(Math.floor(delayMs * 1.5), 15000);
            if (transientErrs >= 30) {
              throw new Error(labels.phase2realization.serverBusy);
            }
            continue;
          }

          if (!statusRes.ok) {
            if (await tryRestoreCompletedRealization()) return;
            throw await createResponseError(statusRes, realizationFailedMessage);
          }

          transientErrs = 0;
          if (!isJsonResponse(statusRes)) {
            if (await tryRestoreCompletedRealization()) return;
            transientErrs += 1;
            await new Promise((r) => setTimeout(r, delayMs));
            delayMs = Math.min(Math.floor(delayMs * 1.5), 15000);
            if (transientErrs >= 30) {
              throw new Error(labels.phase2realization.serverBusy);
            }
            continue;
          }
          const statusData = await statusRes.json();
          if (statusData.status === 'completed' && Array.isArray(statusData.realization_methods)) {
            setMethods(statusData.realization_methods);
            setSelectedIds(statusData.realization_methods.map((m: RealizationMethod) => m.id));
            return;
          }

          if (statusData.status === 'failed') {
            const detail =
              (typeof statusData.error_message === 'string' && statusData.error_message.trim()) ||
              labels.phase2realization.extractionError;
            throw new UserFacingError('realization_failed', detail);
          }

          if (statusData.status === 'not_started') {
            // Backend hasn't started yet; wait and retry
            await new Promise((r) => setTimeout(r, delayMs));
            delayMs = Math.min(delayMs * 1.3, 10000);
            continue;
          }

          if (await tryRestoreCompletedRealization()) return;
          throw new UserFacingError('realization_failed', realizationFailedMessage);
        }
      };

      const response = await fetchWithAuth(
        `/api/phase2/extract-realization-methods?session_id=${encodeURIComponent(sessionId)}`,
        { method: 'POST' }
      );

      if (response.status === 202) {
        await pollStatus();
        return;
      }

      if (isTransientHttpStatus(response.status)) {
        // The request may have reached the backend but the gateway responded with a transient error.
        // Fall back to status polling.
        await new Promise((r) => setTimeout(r, 1500));
        await pollStatus();
        return;
      }

      if (!response.ok) {
        if (await tryRestoreCompletedRealization()) return;
        throw await createResponseError(response, labels.phase2realization.extractionStartError);
      }

      // CloudFront/ALB can return an HTML error page (sometimes even with 200).
      // Avoid crashing on JSON parse; fall back to status polling.
      if (!isJsonResponse(response)) {
        await new Promise((r) => setTimeout(r, 1500));
        await pollStatus();
        return;
      }

      const data = await response.json();
      setMethods(data.realization_methods);
      setSelectedIds(data.realization_methods.map((m: RealizationMethod) => m.id));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(labels.phase2realization.extractionError + ':', error);
      alertUI(getErrorMessage(error, labels.phase2realization.extractionError));
    } finally {
      setLoading(false);
      if (!preserveGlobalLoading && !resumeFlowActiveRef.current) {
        setIsLoading(false);
      }
      extractInFlightRef.current = false;
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handleProceed = async () => {
    if (selectedIds.length === 0) {
      if (showAlert) {
        showAlert(labels.phase2realization.selectAtLeastOne);
      } else {
        window.alert(labels.phase2realization.selectAtLeastOne);
      }
      return;
    }

    if (MOCK_MODE) {
      onComplete({ session_id: sessionId, selected_realization_ids: selectedIds });
      return;
    }

    // 連打・再発火ガード（保険）
    if (proceedInFlightRef.current) return;
    proceedInFlightRef.current = true;

    setProcessing(true);
    setIsLoading(true);
    setLoadingMessage(labels.phase2realization.analysisExecuting);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000000);

    try {
      const pollUntilDone = async (statusUrl: string, onDone?: (data: unknown) => void) => {
        let delayMs = 2000;
        for (;;) {
          if (!mountedRef.current) return;

          const statusRes = await fetchWithAuth(statusUrl, {
            method: 'GET',
            signal: controller.signal,
          });

          if (statusRes.status === 202) {
            await new Promise((r) => setTimeout(r, delayMs));
            delayMs = Math.min(delayMs * 1.3, 10000);
            continue;
          }

          if (!statusRes.ok) throw new Error(labels.phase2realization.processingError);

          const statusData = await statusRes.json();
          if (statusData.status === 'completed') {
            if (onDone) onDone(statusData);
            return;
          }

          if (statusData.status === 'not_started') {
            await new Promise((r) => setTimeout(r, delayMs));
            delayMs = Math.min(delayMs * 1.3, 10000);
            continue;
          }

          throw new Error(labels.phase2realization.processingError);
        }
      };

      // Select methods
      const selRes = await fetchWithAuth(
        `/api/phase2/select-realization-methods?session_id=${encodeURIComponent(sessionId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(selectedIds),
          signal: controller.signal,
        }
      );
      if (!selRes.ok) throw new Error(labels.phase2realization.selectError);

      setLoadingMessage(labels.phase2realization.componentsExtracting);
      const componentsRes = await fetchWithAuth(
        `/api/phase2/extract-components?session_id=${encodeURIComponent(sessionId)}`,
        { method: 'POST', signal: controller.signal }
      );
      if (componentsRes.status === 202) {
        await pollUntilDone(
          `/api/phase2/extract-components/status?session_id=${encodeURIComponent(sessionId)}`
        );
      } else {
        if (!componentsRes.ok) throw new Error(labels.phase2realization.componentsError);
        await componentsRes.json();
      }

      setLoadingMessage(labels.phase2realization.qualityJudging);
      const qualityRes = await fetchWithAuth(
        `/api/phase2/judge-patent-quality?session_id=${encodeURIComponent(sessionId)}`,
        { method: 'POST', signal: controller.signal }
      );
      if (qualityRes.status === 202) {
        await pollUntilDone(
          `/api/phase2/judge-patent-quality/status?session_id=${encodeURIComponent(sessionId)}`,
          (statusData) => onComplete(statusData)
        );
      } else {
        if (!qualityRes.ok) throw new Error(labels.phase2realization.qualityError);
        const qualityData = await qualityRes.json();
        onComplete(qualityData);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('処理に失敗しました:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        alertUI(labels.phase2realization.analysisTimeout);
      } else {
        alertUI(error instanceof Error ? error.message : labels.phase2realization.processingError);
      }
    } finally {
      clearTimeout(timeoutId);
      controller.abort();
      proceedInFlightRef.current = false;
      setProcessing(false);
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  if (loading) {
    return (
      <div className="phase2-realization-selection">
        <div className="phase-card">
          <div className="loading-state">
            <span className="spinner"></span>
            {inlineLoadingMessage}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="phase2-realization-selection">
      <div className="phase-card">
        <h2 className="phase-title">
          <span className="phase-title-step">
            <EditableLabel labelKey="phase2realization.stepLabel" defaultValue="Step3:" />
          </span>
          <span className="phase-title-text">
            <EditableLabel labelKey="phase2realization.title" defaultValue="特許マップ作成" />
          </span>
        </h2>
        <div className="workflow-step-description phase-description-banner">
          <EditableLabel
            labelKey="phase2realization.description"
            defaultValue="特許マップに掲載する技術項目を選定してください"
          />
        </div>

        <div className="selection-summary">
          <span className="summary-text">
            {methods.length}件中 {selectedIds.length}件選択
          </span>
          <button
            onClick={() => setSelectedIds(methods.map((m) => m.id))}
            className="select-all-btn"
          >
            <EditableLabel labelKey="phase2realization.selectAllButton" defaultValue="全て選択" />
          </button>
          <button onClick={() => setSelectedIds([])} className="clear-all-btn">
            <EditableLabel labelKey="phase2realization.clearAllButton" defaultValue="選択解除" />
          </button>
        </div>

        <div className="methods-grid">
          {methods.map((method) => (
            <div
              key={method.id}
              className={`method-card ${selectedIds.includes(method.id) ? 'selected' : ''}`}
              onClick={() => toggleSelection(method.id)}
            >
              <div className="method-header">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(method.id)}
                  onChange={() => {}}
                  className="method-checkbox"
                />
                <span className="patent-count">
                  {method.patent_count}
                  <EditableLabel labelKey="phase2realization.patentCountUnit" defaultValue="件" />
                </span>
              </div>
              <h3 className="method-name">{method.name}</h3>
              <p className="method-description">{method.description}</p>
            </div>
          ))}
        </div>

        <div className="nav-actions">
          <button onClick={handleProceed} className="nav-btn nav-btn-primary" disabled={processing}>
            {processing ? (
              <>
                <span className="spinner"></span>
                <EditableLabel
                  labelKey="phase2realization.processingButton"
                  defaultValue="特許マップを作成しています"
                />
              </>
            ) : (
              <EditableLabel
                labelKey="phase2realization.proceedButton"
                defaultValue="特許マップの作成開始（選択した項目を軸とした構成の特許マップを作成)"
              />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Phase2RealizationSelection;
